
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Media } from '@/types';

interface InternalCycleConfig {
    internal: number;
    external: number;
}

interface UseVenueAdsReturn {
    venueAds: Media[];
    cycleConfig: InternalCycleConfig;
    loading: boolean;
    refetch: () => Promise<void>;
}

export const useVenueAds = (userId?: string | null): UseVenueAdsReturn => {
    const [venueAds, setVenueAds] = useState<Media[]>([]);
    const [cycleConfig, setCycleConfig] = useState<InternalCycleConfig>({ internal: 2, external: 1 });
    const [loading, setLoading] = useState(false);

    const fetchVenueAds = useCallback(async () => {
        if (!userId) {
            setVenueAds([]);
            return;
        }

        try {
            setLoading(true);
            console.log('🔍 [useVenueAds] Buscando parceiro para userId:', userId);

            // 1. Buscar o Venue Partner associado a este usuário
            const { data: partnerData, error: partnerError } = await supabase
                .from('venue_partners')
                .select('id, internal_media_cycle, external_media_cycle')
                .eq('user_id', userId)
                .single();

            if (partnerError && partnerError.code !== 'PGRST116') {
                console.error('❌ [useVenueAds] Erro ao buscar parceiro:', partnerError);
                throw partnerError;
            }

            if (!partnerData) {
                console.log('⚠️ [useVenueAds] Usuário não é um parceiro venue registrado.');
                setVenueAds([]);
                return;
            }

            // Atualizar configuração de ciclo
            setCycleConfig({
                internal: partnerData.internal_media_cycle || 2,
                external: partnerData.external_media_cycle || 1
            });

            console.log('✅ [useVenueAds] Parceiro encontrado:', partnerData.id);
            console.log('⚙️ [useVenueAds] Ciclo configurado:', {
                internal: partnerData.internal_media_cycle || 2,
                external: partnerData.external_media_cycle || 1
            });

            // 2. Buscar campanhas ativas para este parceiro
            // As campanhas devem estar com status 'active' e dentro do prazo
            // Usar apenas a data (YYYY-MM-DD) para comparação correta
            const today = new Date();
            const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD

            console.log('📅 [useVenueAds] Data atual para verificação:', todayStr);

            const { data: campaignsData, error: campaignsError } = await supabase
                .from('venue_campaigns')
                .select('media_ids, media_display_times, start_date, end_date, campaign_name')
                .eq('venue_partner_id', partnerData.id)
                .eq('ads_status', 'active')
                .lte('start_date', todayStr)  // Campanha já começou (start_date <= hoje)
                .gte('end_date', todayStr);   // Campanha ainda não terminou (end_date >= hoje)

            if (campaignsError) {
                console.error('❌ [useVenueAds] Erro ao buscar campanhas:', campaignsError);
                throw campaignsError;
            }

            if (!campaignsData || campaignsData.length === 0) {
                console.log('ℹ️ [useVenueAds] Nenhuma campanha ativa encontrada para a data:', todayStr);
                setVenueAds([]);
                return;
            }

            // Log das campanhas encontradas
            console.log(`✅ [useVenueAds] ${campaignsData.length} campanha(s) ativa(s) encontrada(s):`);
            campaignsData.forEach((c: any) => {
                console.log(`  📢 ${c.campaign_name || 'Sem nome'}: ${c.start_date} → ${c.end_date}`);
            });

            // 3. Coletar todos os media_ids e seus tempos de exibição configurados
            const allMediaIds: string[] = [];
            const mediaDisplayTimesMap: Record<string, number> = {};

            campaignsData.forEach(campaign => {
                const campaignMediaIds = campaign.media_ids || [];
                const campaignDisplayTimes = campaign.media_display_times || {};

                campaignMediaIds.forEach((mediaId: string) => {
                    if (!allMediaIds.includes(mediaId)) {
                        allMediaIds.push(mediaId);
                    }
                    // Priorizar o tempo configurado pelo anunciante
                    if (campaignDisplayTimes[mediaId]) {
                        mediaDisplayTimesMap[mediaId] = campaignDisplayTimes[mediaId];
                    }
                });
            });

            if (allMediaIds.length === 0) {
                setVenueAds([]);
                return;
            }

            console.log('📦 [useVenueAds] IDs de mídia encontrados:', allMediaIds);
            console.log('⏱️ [useVenueAds] Tempos de exibição configurados:', mediaDisplayTimesMap);

            // 4. Buscar as mídias reais
            const { data: mediasData, error: mediasError } = await supabase
                .from('medias')
                .select('*')
                .in('id', allMediaIds)
                .eq('active', true);

            if (mediasError) {
                console.error('❌ [useVenueAds] Erro ao buscar mídias:', mediasError);
                throw mediasError;
            }

            // 5. Aplicar os tempos de exibição configurados pelo anunciante
            const mediasWithCustomDisplayTime = (mediasData || []).map(media => {
                const customDisplayTime = mediaDisplayTimesMap[media.id];
                if (customDisplayTime) {
                    console.log(`⏱️ [useVenueAds] Aplicando tempo personalizado para mídia ${media.title}: ${customDisplayTime}s (original: ${media.display_time}ms)`);
                    return {
                        ...media,
                        display_time: customDisplayTime * 1000 // Converter segundos para milissegundos
                    };
                }
                return media;
            });

            console.log('🎬 [useVenueAds] Mídias externas carregadas:', mediasWithCustomDisplayTime?.length);
            setVenueAds(mediasWithCustomDisplayTime || []);

        } catch (error) {
            console.error('❌ [useVenueAds] Erro geral:', error);
            setVenueAds([]);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchVenueAds();
    }, [fetchVenueAds]);

    return {
        venueAds,
        cycleConfig,
        loading,
        refetch: fetchVenueAds
    };
};
